import React, { useState, useEffect } from 'react';
import {  Box,  Typography,  Paper,  Button,  Table,  TableBody,  TableCell,  TableContainer,  TableHead,  TableRow,  TablePagination,  IconButton,  Chip,  Dialog,  DialogTitle,  DialogContent,  DialogActions,  TextField,  Tooltip,  Alert,  Snackbar,  CircularProgress,  Card,  CardContent,  Accordion,  AccordionSummary,  AccordionDetails,  List,  ListItem,  ListItemText,  ListItemSecondaryAction,  Divider,  FormControl,  InputLabel,  Select,  MenuItem} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Class as ClassIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  ExpandMore as ExpandMoreIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { ClassService } from '../../services/classService';
import { UserService } from '../../services/userService';
import { Class, Student, ClassFormData, StudentFormData, User } from '../../types';

interface ClassStats {  totalClasses: number;  totalStudents: number;  averageClassSize: number;}

export function ClassManagement() {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openClassDialog, setOpenClassDialog] = useState(false);
  const [openStudentDialog, setOpenStudentDialog] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'class' | 'student', item: Class | Student } | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [classStats, setClassStats] = useState<ClassStats>({ totalClasses: 0, totalStudents: 0, averageClassSize: 0 });
  const [facultyUsers, setFacultyUsers] = useState<User[]>([]);
  const [bulkImportDialog, setBulkImportDialog] = useState(false);
  const [bulkStudentData, setBulkStudentData] = useState('');

  const [classFormData, setClassFormData] = useState<ClassFormData>({
    name: '',
    strength: 0,
    students: [],
    subjects: []
  });

  const [studentFormData, setStudentFormData] = useState<StudentFormData>({
    name: '',
    rollNumber: '',
    email: '',
    phone: ''
  });

      useEffect(() => {    loadClasses();    loadFacultyUsers();  }, [searchTerm]);

  const loadClasses = async () => {
    try {
      setLoading(true);
      let result;
      
      if (searchTerm.trim()) {
        result = { classes: await ClassService.searchClasses(searchTerm), hasMore: false };
      } else {
        result = await ClassService.getClasses(100);
      }

      setClasses(result.classes);
      calculateStats(result.classes); // Use all classes for stats
    } catch (error) {
      showSnackbar('Failed to load classes', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadFacultyUsers = async () => {
    try {
      const faculty = await UserService.getUsersByRole('faculty');
      setFacultyUsers(faculty);
    } catch (error) {
      console.error('Failed to load faculty users:', error);
    }
  };

    const calculateStats = (allClasses: Class[]) => {    const stats = allClasses.reduce((acc, classItem) => {      acc.totalClasses++;      acc.totalStudents += classItem.students.length;      return acc;    }, { totalClasses: 0, totalStudents: 0, averageClassSize: 0 });        stats.averageClassSize = stats.totalClasses > 0 ? Math.round(stats.totalStudents / stats.totalClasses) : 0;    setClassStats(stats);  };

  const handleCreateClass = () => {
    setEditingClass(null);
    setClassFormData({
      name: '',
      strength: 0,
      students: [],
      subjects: []
    });
    setOpenClassDialog(true);
  };

  const handleEditClass = (classItem: Class) => {
    setEditingClass(classItem);
    setClassFormData({
      name: classItem.name,
      strength: classItem.strength,
      students: classItem.students.map(s => ({
        name: s.name,
        rollNumber: s.rollNumber,
        email: s.email,
        phone: s.phone
      })),
      subjects: classItem.subjects?.map(s => s.subjectId) || []
    });
    setOpenClassDialog(true);
  };

  const handleAddStudent = (classItem: Class) => {
    setSelectedClass(classItem);
    setEditingStudent(null);
    setStudentFormData({
      name: '',
      rollNumber: '',
      email: '',
      phone: ''
    });
    setOpenStudentDialog(true);
  };

  const handleEditStudent = (student: Student, classItem: Class) => {
    setSelectedClass(classItem);
    setEditingStudent(student);
    setStudentFormData({
      name: student.name,
      rollNumber: student.rollNumber,
      email: student.email || '',
      phone: student.phone || ''
    });
    setOpenStudentDialog(true);
  };

  const handleDeleteClass = (classItem: Class) => {
    setItemToDelete({ type: 'class', item: classItem });
    setDeleteDialogOpen(true);
  };

  const handleDeleteStudent = (student: Student) => {
    setItemToDelete({ type: 'student', item: student });
    setDeleteDialogOpen(true);
  };

  const handleSubmitClass = async () => {
    try {
      // Validation
      if (!classFormData.name.trim()) {
        showSnackbar('Class name is required', 'error');
        return;
      }
      
      if (classFormData.strength <= 0) {
        showSnackbar('Class capacity must be greater than 0', 'error');
        return;
      }

      if (classFormData.strength > 200) {
        showSnackbar('Class capacity cannot exceed 200 students', 'error');
        return;
      }

      if (editingClass) {
        await ClassService.updateClass(editingClass.id, {
          name: classFormData.name.trim(),
          strength: classFormData.strength
        });
        showSnackbar('Class updated successfully', 'success');
      } else {
        // Check for duplicate class names
        const existingClass = classes.find(c => 
          c.name.toLowerCase() === classFormData.name.trim().toLowerCase()
        );
        if (existingClass) {
          showSnackbar('A class with this name already exists', 'error');
          return;
        }

        await ClassService.createClass({
          ...classFormData,
          name: classFormData.name.trim()
        });
        showSnackbar('Class created successfully', 'success');
      }
      setOpenClassDialog(false);
      loadClasses();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to save class', 'error');
    }
  };

  const handleSubmitStudent = async () => {
    if (!selectedClass) return;
    
    try {
      // Validation
      if (!studentFormData.name.trim()) {
        showSnackbar('Student name is required', 'error');
        return;
      }

      if (!studentFormData.rollNumber.trim()) {
        showSnackbar('Roll number is required', 'error');
        return;
      }

      // Email validation
      if (studentFormData.email && studentFormData.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(studentFormData.email.trim())) {
          showSnackbar('Please enter a valid email address', 'error');
          return;
        }
      }

      // Phone validation
      if (studentFormData.phone && studentFormData.phone.trim()) {
        const phoneRegex = /^[\d\s\-\+\(\)]+$/;
        if (!phoneRegex.test(studentFormData.phone.trim()) || studentFormData.phone.trim().length < 10) {
          showSnackbar('Please enter a valid phone number (min 10 digits)', 'error');
          return;
        }
      }

      // Check for duplicate roll numbers in the same class
      if (!editingStudent) {
        const existingStudent = selectedClass.students.find(s => 
          s.rollNumber.toLowerCase() === studentFormData.rollNumber.trim().toLowerCase()
        );
        if (existingStudent) {
          showSnackbar('A student with this roll number already exists in this class', 'error');
          return;
        }
      } else {
        // When editing, check for duplicates excluding the current student
        const existingStudent = selectedClass.students.find(s => 
          s.id !== editingStudent.id && 
          s.rollNumber.toLowerCase() === studentFormData.rollNumber.trim().toLowerCase()
        );
        if (existingStudent) {
          showSnackbar('Another student with this roll number already exists in this class', 'error');
          return;
        }
      }

      // Check class capacity
      if (!editingStudent && selectedClass.students.length >= selectedClass.strength) {
        showSnackbar('Class has reached maximum capacity', 'error');
        return;
      }

      const cleanedData = {
        name: studentFormData.name.trim(),
        rollNumber: studentFormData.rollNumber.trim(),
        email: studentFormData.email?.trim() || '',
        phone: studentFormData.phone?.trim() || ''
      };

      if (editingStudent) {
        await ClassService.updateStudent(editingStudent.id, cleanedData);
        showSnackbar('Student updated successfully', 'success');
      } else {
        await ClassService.createStudent({
          ...cleanedData,
          classId: selectedClass.id
        });
        showSnackbar('Student added successfully', 'success');
      }
      setOpenStudentDialog(false);
      loadClasses();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to save student', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (itemToDelete.type === 'class') {
        await ClassService.deleteClass((itemToDelete.item as Class).id);
        showSnackbar('Class deleted successfully', 'success');
      } else {
        await ClassService.deleteStudent((itemToDelete.item as Student).id);
        showSnackbar('Student deleted successfully', 'success');
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadClasses();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to delete item', 'error');
    }
  };

  const addStudentToForm = () => {
    if (studentFormData.name && studentFormData.rollNumber) {
      setClassFormData({
        ...classFormData,
        students: [...classFormData.students, { ...studentFormData }]
      });
      setStudentFormData({
        name: '',
        rollNumber: '',
        email: '',
        phone: ''
      });
    }
  };

  const removeStudentFromForm = (index: number) => {
    setClassFormData({
      ...classFormData,
      students: classFormData.students.filter((_, i) => i !== index)
    });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleBulkImport = () => {
    setBulkImportDialog(true);
    setBulkStudentData('');
  };

  const processBulkImport = async () => {
    if (!selectedClass || !bulkStudentData.trim()) {
      showSnackbar('Please enter student data', 'error');
      return;
    }

    try {
      const lines = bulkStudentData.trim().split('\n');
      const students = [];
      const errors = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const parts = line.split(',').map(p => p.trim());
        if (parts.length < 2) {
          errors.push(`Line ${i + 1}: Invalid format. Expected: Name, Roll Number, Email (optional), Phone (optional)`);
          continue;
        }

        const [name, rollNumber, email, phone] = parts;

        // Validation
        if (!name || !rollNumber) {
          errors.push(`Line ${i + 1}: Name and Roll Number are required`);
          continue;
        }

        // Check for duplicate roll numbers
        const isDuplicate = selectedClass.students.some(s => 
          s.rollNumber.toLowerCase() === rollNumber.toLowerCase()
        );
        if (isDuplicate) {
          errors.push(`Line ${i + 1}: Roll number ${rollNumber} already exists`);
          continue;
        }

        // Email validation
        if (email && email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          errors.push(`Line ${i + 1}: Invalid email format`);
          continue;
        }

        students.push({
          name,
          rollNumber,
          email: email || '',
          phone: phone || '',
          classId: selectedClass.id
        });
      }

      if (errors.length > 0) {
        showSnackbar(`Import errors: ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`, 'error');
        return;
      }

      if (students.length === 0) {
        showSnackbar('No valid student data found', 'error');
        return;
      }

      // Check capacity
      if (selectedClass.students.length + students.length > selectedClass.strength) {
        showSnackbar(`Cannot import ${students.length} students. Class capacity would be exceeded.`, 'error');
        return;
      }

      // Import students one by one
      let successCount = 0;
      for (const student of students) {
        try {
          await ClassService.createStudent(student);
          successCount++;
        } catch (error) {
          console.error('Error importing student:', error);
        }
      }

      setBulkImportDialog(false);
      setBulkStudentData('');
      loadClasses();
      
      if (successCount === students.length) {
        showSnackbar(`Successfully imported ${successCount} students`, 'success');
      } else {
        showSnackbar(`Imported ${successCount} out of ${students.length} students`, 'warning');
      }

    } catch (error: any) {
      showSnackbar(error.message || 'Failed to import students', 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Class Management
      </Typography>

      {/* Stats Cards */}      <Grid container spacing={3} sx={{ mb: 3 }}>        <Grid size={{ xs: 12, sm: 6, md: 3 }}>          <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>            <CardContent sx={{ pb: 2 }}>              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>                <Box>                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>                    Total Classes                  </Typography>                  <Typography variant="h3" color="primary" sx={{ fontWeight: 700, mb: 1 }}>                    {classStats.totalClasses}                  </Typography>                  <Typography variant="caption" color="textSecondary">                    All classes in system                  </Typography>                </Box>                <Box sx={{                   width: 60,                   height: 60,                   borderRadius: 2,                   bgcolor: 'primary.main',                   display: 'flex',                   alignItems: 'center',                   justifyContent: 'center',                  boxShadow: 2                }}>                  <ClassIcon sx={{ fontSize: 30, color: 'white' }} />                </Box>              </Box>            </CardContent>          </Card>        </Grid>                <Grid size={{ xs: 12, sm: 6, md: 3 }}>          <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>            <CardContent sx={{ pb: 2 }}>              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>                <Box>                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>                    Total Students                  </Typography>                  <Typography variant="h3" color="success.main" sx={{ fontWeight: 700, mb: 1 }}>                    {classStats.totalStudents}                  </Typography>                  <Typography variant="caption" color="textSecondary">                    Enrolled students                  </Typography>                </Box>                <Box sx={{                   width: 60,                   height: 60,                   borderRadius: 2,                   bgcolor: 'success.main',                   display: 'flex',                   alignItems: 'center',                   justifyContent: 'center',                  boxShadow: 2                }}>                  <GroupIcon sx={{ fontSize: 30, color: 'white' }} />                </Box>              </Box>            </CardContent>          </Card>        </Grid>                <Grid size={{ xs: 12, sm: 6, md: 3 }}>          <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>            <CardContent sx={{ pb: 2 }}>              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>                <Box>                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>                    Avg Class Size                  </Typography>                  <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700, mb: 1 }}>                    {classStats.averageClassSize}                  </Typography>                  <Typography variant="caption" color="textSecondary">                    Students per class                  </Typography>                </Box>                <Box sx={{                   width: 60,                   height: 60,                   borderRadius: 2,                   bgcolor: 'warning.main',                   display: 'flex',                   alignItems: 'center',                   justifyContent: 'center',                  boxShadow: 2                }}>                  <PersonIcon sx={{ fontSize: 30, color: 'white' }} />                </Box>              </Box>            </CardContent>          </Card>        </Grid>      </Grid>

      <Paper sx={{ p: 3 }}>
        {/* Actions Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search classes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 250 }}
            />
            <IconButton onClick={loadClasses} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClass}
          >
            Add Class
          </Button>
        </Box>

        {/* Classes List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : classes.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="textSecondary">No classes found</Typography>
          </Box>
        ) : (
          classes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((classItem) => (
            <Accordion key={classItem.id} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <ClassIcon color="primary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="medium">
                      {classItem.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {classItem.students.length} students • Capacity: {classItem.strength}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit Class">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditClass(classItem); }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add Student">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAddStudent(classItem); }}>
                        <PersonAddIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Bulk Import Students">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedClass(classItem); handleBulkImport(); }}>
                        <GroupIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Class">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteClass(classItem); }} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon /> Students ({classItem.students.length})
                </Typography>
                {classItem.students.length === 0 ? (
                  <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No students in this class
                  </Typography>
                ) : (
                  <List>
                    {classItem.students.map((student, index) => (
                      <div key={student.id}>
                        <ListItem>
                          <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body1" fontWeight="medium">
                                  {student.name}
                                </Typography>
                                <Chip label={student.rollNumber} size="small" variant="outlined" />
                              </Box>
                            }
                            secondary={
                              <Box>
                                {student.email && (
                                  <Typography variant="caption" display="block">
                                    Email: {student.email}
        </Typography>
                                )}
                                {student.phone && (
                                  <Typography variant="caption" display="block">
                                    Phone: {student.phone}
        </Typography>
                                )}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Edit Student">
                              <IconButton size="small" onClick={() => handleEditStudent(student, classItem)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Student">
                              <IconButton size="small" onClick={() => handleDeleteStudent(student)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < classItem.students.length - 1 && <Divider />}
                      </div>
                    ))}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}

        {classes.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={classes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </Paper>

      {/* Class Form Dialog */}
      <Dialog open={openClassDialog} onClose={() => setOpenClassDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingClass ? 'Edit Class' : 'Create New Class'}
        </DialogTitle>
                        <DialogContent>          <Grid container spacing={2} sx={{ mt: 1 }}>            <Grid size={{ xs: 12, md: 6 }}>              <TextField                fullWidth                label="Class Name"                value={classFormData.name}                onChange={(e) => setClassFormData({ ...classFormData, name: e.target.value })}                required              />            </Grid>            <Grid size={{ xs: 12, md: 6 }}>              <TextField                fullWidth                label="Capacity"                type="number"                value={classFormData.strength}                onChange={(e) => setClassFormData({ ...classFormData, strength: parseInt(e.target.value) || 0 })}                required              />            </Grid>                        {!editingClass && (              <>                <Grid size={12}>                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>                    Add Students                  </Typography>                </Grid>                <Grid size={{ xs: 12, md: 6 }}>                  <TextField                    fullWidth                    label="Student Name"                    value={studentFormData.name}                    onChange={(e) => setStudentFormData({ ...studentFormData, name: e.target.value })}                  />                </Grid>                <Grid size={{ xs: 12, md: 6 }}>                  <TextField                    fullWidth                    label="Roll Number"                    value={studentFormData.rollNumber}                    onChange={(e) => setStudentFormData({ ...studentFormData, rollNumber: e.target.value })}                  />                </Grid>                <Grid size={{ xs: 12, md: 6 }}>                  <TextField                    fullWidth                    label="Email"                    type="email"                    value={studentFormData.email}                    onChange={(e) => setStudentFormData({ ...studentFormData, email: e.target.value })}                  />                </Grid>                <Grid size={{ xs: 12, md: 6 }}>                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>                    <TextField                      fullWidth                      label="Phone"                      value={studentFormData.phone}                      onChange={(e) => setStudentFormData({ ...studentFormData, phone: e.target.value })}                    />                    <Button variant="outlined" onClick={addStudentToForm}>                      Add                    </Button>                  </Box>                </Grid>                                {classFormData.students.length > 0 && (                  <Grid size={12}>                    <Typography variant="subtitle1" sx={{ mb: 1 }}>                      Students to be added ({classFormData.students.length}):                    </Typography>                    <List>                      {classFormData.students.map((student, index) => (                        <ListItem key={index} divider>                          <ListItemText                            primary={student.name}                            secondary={`Roll: ${student.rollNumber}${student.email ? ` • Email: ${student.email}` : ''}`}                          />                          <ListItemSecondaryAction>                            <IconButton size="small" onClick={() => removeStudentFromForm(index)} color="error">                              <DeleteIcon />                            </IconButton>                          </ListItemSecondaryAction>                        </ListItem>                      ))}                    </List>                  </Grid>                )}              </>            )}          </Grid>        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClassDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitClass} variant="contained">
            {editingClass ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Form Dialog */}
      <Dialog open={openStudentDialog} onClose={() => setOpenStudentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStudent ? 'Edit Student' : 'Add New Student'}
        </DialogTitle>
                        <DialogContent>          <Grid container spacing={2} sx={{ mt: 1 }}>            <Grid size={12}>              <TextField                fullWidth                label="Student Name"                value={studentFormData.name}                onChange={(e) => setStudentFormData({ ...studentFormData, name: e.target.value })}                required              />            </Grid>            <Grid size={12}>              <TextField                fullWidth                label="Roll Number"                value={studentFormData.rollNumber}                onChange={(e) => setStudentFormData({ ...studentFormData, rollNumber: e.target.value })}                required              />            </Grid>            <Grid size={12}>              <TextField                fullWidth                label="Email"                type="email"                value={studentFormData.email}                onChange={(e) => setStudentFormData({ ...studentFormData, email: e.target.value })}              />            </Grid>            <Grid size={12}>              <TextField                fullWidth                label="Phone"                value={studentFormData.phone}                onChange={(e) => setStudentFormData({ ...studentFormData, phone: e.target.value })}              />            </Grid>          </Grid>        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStudentDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitStudent} variant="contained">
            {editingStudent ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {itemToDelete?.type}? 
            {itemToDelete?.type === 'class' && ' All students in this class will also be deleted.'}
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 